import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaWallet, FaArrowUp, FaArrowDown, FaHistory, FaSync, FaExclamationCircle } from "react-icons/fa";

const PressWallet = () => {
  const [walletData, setWalletData] = useState({ balance: 0, transactions: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchWalletData = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/press/wallet/detail`,
        { headers: { Authorization: `Bearer ${localStorage.getItem("pressToken")}` } }
      );
      const newWalletData = {
        balance: response.data.data?.balance || response.data.balance || 0,
        transactions: response.data.data?.transactions || response.data.transactions || [],
      };

      if (walletData.transactions.length > 0 && newWalletData.transactions.length > walletData.transactions.length) {
        const newTransactions = newWalletData.transactions.slice(0, newWalletData.transactions.length - walletData.transactions.length);
        newTransactions.forEach(transaction => {
          if (transaction.type === "credit" && transaction.description?.includes("Refund for rejected paid conference")) {
            toast.success(`💰 Refund received! ₹${transaction.amount} has been added to your wallet.`);
          }
        });
      }

      setWalletData(newWalletData);
    } catch (error) {
      console.error("Error fetching press wallet data:", error);
      setError("Failed to load wallet data");
    }
  };

  useEffect(() => {
    const loadWalletData = async () => {
      setLoading(true);
      setError(null);
      try { await fetchWalletData(); }
      finally { setLoading(false); }
    };
    loadWalletData();

    const refreshInterval = setInterval(fetchWalletData, 30000);
    return () => clearInterval(refreshInterval);
  }, []);

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "completed": return "text-emerald-600 bg-emerald-100";
      case "pending": return "text-amber-600 bg-amber-100";
      case "failed": return "text-red-600 bg-red-100";
      default: return "text-slate-600 bg-slate-100";
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f4f6fb] p-3 sm:p-6 pb-8 space-y-3">
        <div className="h-40 bg-white rounded-[1.5rem] animate-pulse" />
        <div className="h-10 w-32 bg-white rounded-lg animate-pulse mt-6" />
        {[0, 1, 2, 3].map(i => <div key={i} className="h-24 bg-white rounded-2xl animate-pulse" />)}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f4f6fb] p-3 sm:p-6 pb-8">
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar newestOnTop />

      {/* ── Wallet Hero Card ── */}
      <div className="relative bg-gradient-to-br from-indigo-600 via-blue-600 to-cyan-500 rounded-[1.5rem] p-5 sm:p-8 mb-6 overflow-hidden shadow-lg">
        <div className="absolute top-0 right-0 w-48 h-48 bg-white opacity-5 rounded-full blur-2xl -translate-y-1/2 translate-x-1/4" />
        <div className="absolute bottom-0 left-0 w-40 h-40 bg-white opacity-10 rounded-full blur-xl translate-y-1/3 -translate-x-1/3" />

        <div className="relative z-10 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-2 opacity-80 text-white">
              <FaWallet className="w-4 h-4" />
              <span className="text-xs font-black uppercase tracking-widest">Available Balance</span>
            </div>
            <div className="flex items-baseline gap-1">
              <span className="text-3xl sm:text-5xl font-black text-white">₹{walletData.balance.toFixed(2)}</span>
            </div>
            <p className="text-white/70 text-xs font-medium mt-1">Manage conference payments and refunds</p>
          </div>

          <div className="flex sm:flex-col gap-2">
            <button onClick={fetchWalletData}
              className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 py-3 sm:py-2.5 bg-white/20 hover:bg-white/30 text-white rounded-xl text-sm font-black backdrop-blur-md transition-all active:scale-95 border border-white/20 text-center">
              <FaSync className="w-3.5 h-3.5" /> Refresh
            </button>
            <button disabled title="Withdrawals are currently disabled"
              className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 py-3 sm:py-2.5 bg-white/10 text-white/50 rounded-xl text-sm font-black backdrop-blur-md cursor-not-allowed border border-white/10 text-center">
              Withdraw
            </button>
          </div>
        </div>
      </div>

      {/* ── Transactions Section ── */}
      <div className="flex items-center justify-between mb-4 px-1">
        <h2 className="text-lg font-black text-slate-800 flex items-center gap-2">
          <FaHistory className="text-slate-400" /> Recent Transactions
        </h2>
        <span className="text-xs font-bold text-slate-500 bg-slate-200/50 px-2.5 py-1 rounded-lg">
          {walletData.transactions.length} Total
        </span>
      </div>

      {error ? (
        <div className="bg-red-50 text-red-600 p-4 rounded-2xl flex items-start gap-3 border border-red-100">
          <FaExclamationCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <p className="text-sm font-semibold">{error}</p>
        </div>
      ) : walletData.transactions.length === 0 ? (
        <div className="bg-white rounded-2xl p-10 flex flex-col items-center justify-center text-center shadow-sm border border-slate-50">
          <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mb-3">
            <FaHistory className="w-6 h-6 text-slate-300" />
          </div>
          <p className="text-base font-black text-slate-700">No Transactions Yet</p>
          <p className="text-sm text-slate-500 mt-1 max-w-xs">Your payment history and refunds will appear here once you start using your wallet.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {[...walletData.transactions].reverse().map((tx, idx) => {
            const isDebit = tx.status?.toLowerCase() === "debit" || tx.type === "debit";
            return (
              <div key={idx} className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100 flex items-center gap-4 hover:shadow-md transition-shadow">

                {/* Icon */}
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${isDebit ? "bg-red-50" : "bg-emerald-50"}`}>
                  {isDebit ? <FaArrowUp className="w-5 h-5 text-red-500" /> : <FaArrowDown className="w-5 h-5 text-emerald-500" />}
                </div>

                {/* Details */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <p className="text-sm font-black text-slate-800 truncate">{tx.description || (isDebit ? "Payment Sent" : "Money Received")}</p>
                    <span className={`text-base font-black flex-shrink-0 ${isDebit ? "text-red-600" : "text-emerald-600"}`}>
                      {isDebit ? "-" : "+"}₹{Math.abs(tx.amount).toFixed(2)}
                    </span>
                  </div>

                  <div className="flex items-center gap-2 text-xs text-slate-500 flex-wrap">
                    <span className="font-semibold">{new Date(tx.date).toLocaleDateString("en-IN", { month: "short", day: "numeric", year: "numeric" })}</span>
                    <span>•</span>
                    <span>{new Date(tx.date).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}</span>
                    <span>•</span>
                    <span className="font-mono text-[10px] text-slate-400">ID: {tx._id}</span>
                  </div>

                  {/* Badges / Extras */}
                  <div className="flex items-center gap-2 mt-2 flex-wrap">
                    <span className={`text-[10px] font-black px-2 py-0.5 rounded-md uppercase tracking-wider ${getStatusColor(tx.status || tx.type)}`}>
                      {tx.status || tx.type}
                    </span>
                    {tx.rejectionReason && (
                      <span className="text-[10px] font-bold text-red-600 bg-red-50 px-2 py-0.5 rounded-md truncate max-w-[200px]">
                        Reason: {tx.rejectionReason}
                      </span>
                    )}
                    {tx.refundId && (
                      <span className="text-[10px] font-bold text-blue-600 bg-blue-50 px-2 py-0.5 rounded-md uppercase font-mono">
                        Refund: {tx.refundId}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PressWallet;
